// Coordinate parsing for the paste-first CoordinateField (the field-reality
// path: surveyors share map links / "lat, long" strings; the site pin is typed
// or pasted, never picked on a map until the air-gap map engine lands with
// monitoring). Pure string parsing — zero dependencies, zero network (ADR-013).
//
// Accepted forms (first match wins):
//   • Google Maps links — the place deep-param (`!3d<lat>!4d<lng>`), the
//     viewport (`@lat,lng,15z`), and the query params (`q= / query= / ll= /
//     center= / destination=`, comma possibly %2C-encoded).
//   • OpenStreetMap links — `#map=z/lat/lng` and `mlat=…&mlon=…`.
//   • geo: URIs — `geo:lat,lng`.
//   • A bare decimal pair — "24.6748, 46.6250" (comma / semicolon / whitespace
//     separated; dot decimals — DMS and comma-decimals are out of scope v1).
//
// Out-of-range pairs (|lat| > 90, |lng| > 180) return null — never auto-swapped
// (a swap is a guess; the DB CHECKs would reject it anyway). Values round to
// 6 decimals (the schema's numeric(9,6) — ~10 cm).

export interface Coordinates {
  latitude: number;
  longitude: number;
}

const round6 = (n: number) => Math.round(n * 1e6) / 1e6;

const NUM = String.raw`(-?\d{1,3}(?:\.\d+)?)`;

const URL_PATTERNS: RegExp[] = [
  new RegExp(String.raw`!3d${NUM}!4d${NUM}`),
  new RegExp(String.raw`@${NUM},${NUM}`),
  new RegExp(String.raw`[?&](?:q|query|ll|center|destination|daddr|saddr)=${NUM}(?:%2C|,)\s*${NUM}`, 'i'),
  new RegExp(String.raw`#map=\d+(?:\.\d+)?/${NUM}/${NUM}`),
  new RegExp(String.raw`^geo:${NUM},${NUM}`, 'i'),
];

const OSM_MARKER = {
  lat: new RegExp(String.raw`[?&]mlat=${NUM}`, 'i'),
  lng: new RegExp(String.raw`[?&]mlon=${NUM}`, 'i'),
};

const BARE_PAIR = new RegExp(String.raw`^${NUM}\s*[,;\s]\s*${NUM}$`);

function toCoordinates(latText: string | undefined, lngText: string | undefined): Coordinates | null {
  if (latText === undefined || lngText === undefined) return null;
  const latitude = Number(latText);
  const longitude = Number(lngText);
  if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return null;
  if (Math.abs(latitude) > 90 || Math.abs(longitude) > 180) return null;
  return { latitude: round6(latitude), longitude: round6(longitude) };
}

/** Extract a coordinate pair from pasted text; null when nothing valid parses. */
export function parseCoordinates(text: string): Coordinates | null {
  const trimmed = text.trim();
  if (trimmed === '') return null;

  // The OSM MARKER (`mlat/mlon`) is the pin; it must win over the `#map=`
  // viewport the same URL usually also carries (as `!3d!4d` wins over `@`).
  const mlat = OSM_MARKER.lat.exec(trimmed);
  const mlon = OSM_MARKER.lng.exec(trimmed);
  if (mlat && mlon) {
    const parsed = toCoordinates(mlat[1], mlon[1]);
    if (parsed) return parsed;
  }

  for (const pattern of URL_PATTERNS) {
    const m = pattern.exec(trimmed);
    if (m) {
      const parsed = toCoordinates(m[1], m[2]);
      if (parsed) return parsed;
    }
  }

  const bare = BARE_PAIR.exec(trimmed);
  if (bare) return toCoordinates(bare[1], bare[2]);

  return null;
}

/** The canonical display form — "24.6748, 46.625" (Latin digits; a technical value, ADR-030). */
export function formatCoordinates(value: Coordinates): string {
  return `${round6(value.latitude)}, ${round6(value.longitude)}`;
}

/** Value equality at the 6-decimal grain the field emits. */
export function sameCoordinates(a: Coordinates | null, b: Coordinates | null): boolean {
  if (a === null || b === null) return a === b;
  return round6(a.latitude) === round6(b.latitude) && round6(a.longitude) === round6(b.longitude);
}
